import { css } from '@emotion/core';

// This is exported because the topbar needs access to some of these styles for
// full-width its behavior.
export const fullWidthStyle = `
    width: 100%;
    justify-content: left;

    & span.tabItem-visible-label {
      text-align: left;
      min-width: 100%;
    }
    &.cybs-tabItem-hasIcon span.tabItem-visible-label,
    &.cybs-tabItem-isMenu span.tabItem-visible-label {
      min-width: calc(100% - 2rem);
    }
    &.cybs-tabItem-hasIcon.cybs-tabItem-isMenu span.tabItem-visible-label {
      min-width: calc(100% - 4rem);
    }
  `;

// This is exported because the Tab component needs to re-apply the styles,
// thanks to vds TabItem resetting many of these.
// If you have to add new fields to this object, check if they're needed in
// CybsTab too.
export const reflowStyles = {
  default: `
    /* We use flex for improved behavior when wrapping */
    display: inline-flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;

    /* vds sets a static height, which causes wrapping problems.
     * instead, use min-height
     */
    height: initial !important;
    min-height: 2.875rem;

    /* the vds line height is too spacious. bump it down a bit */
    line-height: 1 !important;

    /* ensure some minimum space between content and borders */
    padding: 0.5rem 1rem;

    & > span.tabItem-visible-label {
      /* ensure minimum vertical space when there are icons above
       * (due to wrapping)
       */
      margin: 0.25rem 0;
      padding: 0;
      /* word wrap */
      overflow-wrap: normal;
      word-break: break-word;
    }

    /* remove vds margins which are used for right-justifying */
    & > .vds-tab--action {
      margin: 0px;
      min-width: 2rem;
    }

    &.cybs-tabItem-collapsed {
      flex-wrap: wrap;
    }
  `
};

export default ({ notification, notificationColor, isSelected }) => ({
  tabItemStyles: ({ cybs, vds }) => css`
    &,
    &:hover,
    &:visited,
    &:link,
    &:active {
      text-decoration: none;
    }

    /* Default */
    &.vds-tab.vds-tab {
      margin: 0px;
      color: ${cybs?.base?.wcag21?.charcoal ?? '#000000'};
      border: 0.1px solid transparent;
      .vds-icon use {
        fill: ${cybs?.core?.primary ?? '#1434CB'};
      }

      ${reflowStyles.default}
      &.cybs-tabItem-collapsed span.tabItem-visible-label {
        display: none;
      }

      @media (max-width: 320px) {
        & {
          border-left: 1px solid ${cybs?.base?.wcag21?.stoneGrey?? '#62798D'};
        }
        &:not(.cybs-tabItem-collapsed) {
          ${fullWidthStyle}
        }
      }
    }

    /* Disabled */
    &.vds-tab:disabled,
    &.vds-tab.vds-state--disabled {
      color: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'} !important;
      pointer-events: none;
      box-shadow: none !important;

      & .vds-icon use {
        fill: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
      }

      @media (max-width: 320px) {
        & {
          border-left: 1px solid ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'} !important;
          border-top: none !important;
          border-bottom: none !important;
        }  
      }
    }

    /* Hover */
    &.vds-tab:hover {
      background-color: ${vds?.tableHighlight ?? '#C7EDFF'} !important;
      color: ${cybs?.base?.wcag21?.charcoal ?? '#000000'} !important;
      border: 0.1px solid ${cybs?.base?.wcag21?.charcoal ?? '#000000'};
      position: relative;
      z-index: 1;

      &.vds-state--selected {
        border: 0.1px solid ${vds?.primaryExtraDark2 ?? '#021E4C'};
      }

      & .vds-icon use {
        fill: ${cybs?.core?.primary ?? '#1434CB'};
      }
    }

    /* Focus */
    &.vds-tab:focus,
    &.vds-tab.vds-state--focus {
      color: ${cybs?.base?.wcag21?.charcoal ?? '#000000'} !important;
      text-decoration: none;

      ${!isSelected &&
      `
        & .vds-icon use {
          fill: ${cybs?.core?.primary ?? '#1434CB'};
        }`}
    }

    &.vds-tab.focus-visible,
    html:not(.js-focus-visible) &.vds-tab:focus {
      position: relative;
      outline: 1px dashed ${vds?.primaryExtraDark2 ?? '#021E4C'};
    }

    /* Pressed */
    &.vds-tab.vds-tab:active, &.vds-tab.vds-state--selected {
      box-shadow: 0 -4px 0 ${cybs?.core?.primary ?? '#1434CB'} inset;
      color: ${cybs?.base?.wcag21?.charcoal ?? '#000000'};

      & .vds-icon use {
        fill: ${cybs?.core?.primary ?? '#1434CB'};
      }

    }

    @media (max-width: 320px) {
      &.vds-tab:not(:disabled, .vds-state--disabled) {
        &:active, &.vds-state--selected {
          box-shadow: 4px 0 0 ${cybs?.core?.primary ?? '#1434CB'} inset;
        }
  
        &:active, &.vds-state--selected:hover {
          border: 0.1px solid ${cybs?.base?.wcag21?.stoneGrey?? '#62798D'} !important;
        }
  
        &.vds-state--selected {
          &:not(:hover) {
            border-top: 1px solid ${cybs?.base?.wcag21?.stoneGrey?? '#62798D'} !important;
            border-bottom: 1px solid ${cybs?.base?.wcag21?.stoneGrey?? '#62798D'} !important;
            border-left: none !important;
            border-right: none !important;
          }

          &:focus {
            border: none !important;
          }
        }
      }
    }

    &.vds-tab.vds-tab:active {
      background-color: ${vds?.tableHighlight ?? '#C7EDFF'};
      border: 0.1px solid ${vds?.primaryExtraDark2 ?? '#021E4C'} !important;
    }

    /* Selected Focus */
    &.vds-state--selected:focus {
      background-color: ${vds?.tableHighlight ?? '#e0eafa'};
    }

    /* Container class for vds-icon */
    & .tab-item-icon {
      display: inline-block;
      position: relative;
      height: 1.5rem;
      line-height: 1.5rem;

      ${notification &&
      `
        &::after {
          content: "";
          position: absolute;
          display: block;
          background-color: ${notificationColor};
          width: 0.825rem;
          height: 0.825rem;
          top: 0rem;
          left: 0.75rem;
          border: 0.125rem solid white;
          border-radius: 100%;
          box-sizing: border-box;
        }
      `}
    }
  `
});
